/**
 * Screen 3: what the import did, and the two ways out of it.
 *
 * The rows that did not go in are the point of this screen, not a footnote:
 * somebody has to correct them, and they cannot do that from a summary. So they
 * are grouped by reason, each group says what that reason means, and the whole
 * lot leaves as a file.
 *
 * That file is built here from the result already in hand. It used to be a link
 * at an endpoint, and a link is a browser navigation, and a browser navigation
 * does not carry the bearer token the interceptor puts on XHRs - so the API
 * answered 401 and the person got the browser's own error page over an export
 * that worked perfectly. Nothing here makes a request, so nothing here can lose
 * a token.
 */
import { Component, EventEmitter, Input, Output } from '@angular/core';

import { triggerBlobDownload } from '../../shared/download.util';
import {
  PlanRow,
  TableApplyResult,
  TransferSchema,
} from '../../services/table-import-export.service';
import { groupByReason } from './plan-decisions';
import {
  ReasonCopy,
  notesToShow,
  reasonCopy,
  wasPromised,
} from './reason-copy';
import {
  notImportedBlob,
  notImportedCsv,
  notImportedFilename,
} from './not-imported-file';

export interface NotImportedGroup {
  reason: string;
  rows: PlanRow[];
  copy: ReasonCopy;
}

@Component({
  selector: 'app-import-result-step',
  templateUrl: './import-result-step.component.html',
  styleUrls: ['./import-result-step.component.css'],
})
export class ImportResultStepComponent {
  // Setters rather than ngOnChanges: what the lists are made of should not
  // depend on how the input arrived, and ngOnChanges only fires for a template
  // binding.
  @Input({ required: true })
  set result(value: TableApplyResult) { this.resultValue = value; this.settle(); }
  get result(): TableApplyResult { return this.resultValue; }
  private resultValue!: TableApplyResult;
  @Input({ required: true }) schema!: TransferSchema;
  /** The file this came from, so the download is named after it. */
  @Input() sourceFileName = 'import';
  @Input() undoing = false;

  @Output() undoRequested = new EventEmitter<void>();

  get wasUndone(): boolean { return this.result.operation === 'Undo'; }

  get written(): number { return this.result.created + this.result.updated; }

  /**
   * THE TWO LISTS, settled once per result rather than once per frame.
   *
   * Getters that group rows build new group objects every call, *ngFor
   * compares the ITEMS it is handed, and brand-new groups read as "every row
   * here has been replaced" - so the whole list was torn down and rebuilt on
   * every change detection pass. Same shape as the plan and mapping screens
   * had; this is the third of the three, found by looking rather than by
   * somebody hitting it.
   */
  /** Refused before anything was attempted: "we told you in advance". */
  refused: NotImportedGroup[] = [];

  /** Promised and then not written: "we said we would and we did not". */
  unwritten: NotImportedGroup[] = [];

  get notImportedCount(): number { return this.result.notImported.length; }

  get unwrittenCount(): number { return this.count(this.unwritten); }

  get refusedCount(): number { return this.count(this.refused); }

  /**
   * The summary says this many writes failed; the list below accounts for this
   * many fewer. A number without the rows behind it is the worst of the two
   * failures, because it reads as complete - somebody takes the file away
   * believing it holds everything that did not go in.
   */
  get unlistedFailures(): number {
    const listed = this.result.notImported
      .filter(row => wasPromised(row.reason)).length;
    return Math.max(0, this.result.failed - listed);
  }

  get canUndo(): boolean { return this.result.canUndo && !this.undoing; }

  private settle(): void {
    if (!this.resultValue) return;
    this.refused = this.describe(row => !wasPromised(row.reason));
    this.unwritten = this.describe(row => wasPromised(row.reason));
  }

  notesOn(row: PlanRow): string[] { return notesToShow(row.notes ?? []); }

  noteCopy(code: string): ReasonCopy { return reasonCopy(code); }

  identify(row: PlanRow): string {
    return this.schema.fields
      .map(field => row.values[field.name])
      .filter((value): value is string => !!value && value.trim() !== '')
      .slice(0, 2)
      .join(' — ');
  }

  private count(groups: readonly NotImportedGroup[]): number {
    return groups.reduce((total, group) => total + group.rows.length, 0);
  }

  private describe(keep: (row: PlanRow) => boolean): NotImportedGroup[] {
    return groupByReason(this.result.notImported.filter(keep))
      .map(group => ({ ...group, copy: reasonCopy(group.reason) }));
  }

  /**
   * A download, not a navigation. The distinction has cost a working export
   * once already, so it is asserted in the spec rather than left to the shape
   * of the markup.
   */
  downloadNotImported(): void {
    if (!this.result.notImported.length) return;
    const csv = notImportedCsv(this.result.notImported, this.schema);
    triggerBlobDownload(
      notImportedBlob(csv), notImportedFilename(this.sourceFileName));
  }
}
